import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useGameStore } from '../store/gameStore';
import { decodeBoardState } from '../utils/encoding';

export const useUrlManagement = () => {
    const { boardId } = useParams<{ boardId?: string }>();
    const navigate = useNavigate();
    const gameId = useGameStore((state) => state.gameId);
    const loadBoardFromState = useGameStore((state) => state.loadBoardFromState);
    const loadGameFromFirebase = useGameStore((state) => state.loadGameFromFirebase);
    const subscribeToGame = useGameStore((state) => state.subscribeToGame);
    const unsubscribeFromGame = useGameStore((state) => state.unsubscribeFromGame);

    // Load game state from URL on component mount
    useEffect(() => {
        if (boardId) {
            // First try to load as Firebase game ID
            loadGameFromFirebase(boardId).then((success) => {
                if (success) {
                    // Start real-time subscription
                    subscribeToGame(boardId);
                } else {
                    // Fallback to decode as encoded board state (legacy support)
                    const boardState = decodeBoardState(boardId);
                    if (boardState) {
                        loadBoardFromState(boardState.words, boardState.types, boardState.languageSetId);
                    } else {
                        // Show 404 or error state
                        navigate('/404', { replace: true });
                    }
                }
            }).catch(() => {
                // Try legacy decoding as fallback
                const boardState = decodeBoardState(boardId);
                if (boardState) {
                    loadBoardFromState(boardState.words, boardState.types, boardState.languageSetId);
                } else {
                    navigate('/404', { replace: true });
                }
            });
        }

        // Cleanup subscription on unmount
        return () => {
            unsubscribeFromGame();
        };
    }, [boardId, loadGameFromFirebase, loadBoardFromState, subscribeToGame, unsubscribeFromGame, navigate]);

    // Function to update URL with game ID
    const updateURL = (newGameId: string) => {
        if (newGameId) {
            navigate(`/board/${newGameId}`, { replace: true });
        }
    };

    return { updateURL, gameId };
};
